import { useEffect, useMemo, useState } from "react";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";
import TruncateTooltip from "../../../../../components/common/TruncateTooltip/TruncateTooltip";
import GoogleSignIn from "../../../../../resources/images/google.png";
import { useAppDispatch, useAppSelector } from "../../../../../redux/reduxHooks";
import { fetchAccessTokens } from "../../../../../redux/accessTokens/accessTokens";
import type { QuickConnectTarget } from "../../../hooks/useConnectLauncher";
import AccountsModal, { AccountRemoveButton } from "../AccountsModal/AccountsModal";
import styles from "./QuickConnectModal.module.scss";

const AMAZON_SIGN_IN = "https://images-na.ssl-images-amazon.com/images/G/01/lwa/btnLWA_gold_390x92.png";

/**
 * A quick-connect account as it exists in `access_tokens`. The slice is untyped and the shape varies
 * by provider, so only the fields the legacy disconnect screen read are declared here.
 */
export interface QuickAccount {
  id: number;
  integrationPartner: string;
  storeName?: string | null;
  client_name?: string | null;
  client_id?: string | null;
  customer_name?: string | null;
  customer_id?: string | null;
}

export interface QuickConnectModalProps {
  visible: boolean;
  target: QuickConnectTarget;
  logo?: string | null;
  agencyClientId: number | null;
  onConnect: () => void;
  onRemoveAccount: (account: QuickAccount) => void;
  onClose: () => void;
}

/** Same fallback chain the legacy DisconnectScreen used, so the rows read identically. */
export const quickAccountName = (account: QuickAccount): string =>
  account.storeName || account.client_name || account.customer_name || account.customer_id || "Account";

const quickAccountId = (account: QuickAccount): string | null =>
  account.customer_id || account.client_id || null;

const QuickConnectModal = ({
  visible,
  target,
  logo,
  agencyClientId,
  onConnect,
  onRemoveAccount,
  onClose,
}: QuickConnectModalProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const tokens = useAppSelector((state) => state.accessTokens.accessTokens) as QuickAccount[];
  const channelName = target.companyName ?? "this channel";

  // A host such as the report header may never have loaded the tokens this list is built from.
  useEffect(() => {
    if (!visible) return;
    let active = true;
    setLoaded(false);
    setLoadFailed(false);
    void Promise.resolve(dispatch(fetchAccessTokens(agencyClientId ? `${agencyClientId}` : null) as never))
      .then((result) => {
        if (active) setLoadFailed((result as { meta?: { requestStatus?: string } }).meta?.requestStatus !== "fulfilled");
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    return () => { active = false; };
  }, [agencyClientId, dispatch, reloadKey, target._id, visible]);

  const accounts = useMemo(
    () => (tokens ?? []).filter((token) => token?.integrationPartner === target._id),
    [target._id, tokens]
  );

  const signIn = target.companyName?.includes("Google")
    ? { src: GoogleSignIn, alt: `Sign in with Google to connect ${channelName}` }
    : target.companyName?.includes("Amazon")
      ? { src: AMAZON_SIGN_IN, alt: `Login with Amazon to connect ${channelName}` }
      : null;

  return (
    <AccountsModal
      visible={visible}
      channelName={channelName}
      logo={logo}
      status={!loaded ? "loading" : loadFailed ? "failed" : "ready"}
      count={accounts.length}
      emptyTitle={`Connect Your ${channelName} Account`}
      emptyAction={
        // Mirrors the legacy IpScreen: the provider's own sign-in button where one exists.
        signIn ? (
          <button type="button" className={styles.signInButton} onClick={onConnect} aria-label={signIn.alt}>
            <img className={`${styles.signIn} ${signIn.src === GoogleSignIn ? styles.googleSignIn : ""}`} src={signIn.src} alt="" />
          </button>
        ) : (
          <MeritoButton type="primary-1" btnText={`Connect ${channelName}`} onClick={onConnect} />
        )
      }
      onRetry={() => setReloadKey((key) => key + 1)}
      onConnectNew={onConnect}
      onClose={onClose}
    >
      <ul className={styles.list}>
        {accounts.map((account) => {
          const name = quickAccountName(account);
          const reference = quickAccountId(account);
          return (
            <li className={styles.row} key={account.id}>
              <div className={styles.rowText}>
                <TruncateTooltip text={name} className={styles.primary} />
                {reference ? <span className={styles.secondary}>{reference}</span> : null}
              </div>
              <AccountRemoveButton label={name} onClick={() => onRemoveAccount(account)} />
            </li>
          );
        })}
      </ul>
    </AccountsModal>
  );
};

export default QuickConnectModal;
